/* === OFATab.tsx === */

import React, { useState } from 'react';
import {
    KatRow, KatColumn, KatAlert, KatInput,
    KatButton
} from '@amzn/katal-react';
import './css/TellerTab.css';
import { DropDownHelper } from 'src/components/helper/DropDownHelper';
import {
    countryQuery, createTestcase,
    currencyQuery, expectedResultQuery,
    getOfaProjects, METHOD_POST, operatingUnitQuery,
    paymentMethodQuery, projectQuery
} from 'src/components/constants/constants';

import { getApiBaseUrl } from 'src/config/api';
import {makeApiRequest} from "src/components/helper/apiUtils";

type OFATabProps = {
    onSaveSuccess?: () => void;
};

const OFATab: React.FC<OFATabProps> = ({onSaveSuccess}) => {
    const testPilotApi = getApiBaseUrl();
    const [project, setProject] = useState<string>('');
    const [isAddingProject, setIsAddingProject] = useState<boolean>(false);
    const [description, setDescription] = useState('');
    const [paymentTemplate, setpaymentTemplate] = useState('');
    const [bankAccountNo, setBankAccountNo] = useState('');
    const [country, setCountry] = useState('');
    const [operatingUnit, setOperatingUnit] = useState('');
    const [currency, setCurrency] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');
    const [invoice, setInvoice] = useState('');
    const [expectedResult, setExpectedResult] = useState('');
    const [errors, setErrors] = useState<any>({});
    const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' | null }>({ message: '', type: null });

    const validateForm = () => {
        const newErrors: any = {};
        if (!project) newErrors.project = 'Project is required';
        if (!description) newErrors.description = 'Description is required';
        //if (!country) newErrors.country = 'Country is required';
        //if (!operatingUnit) newErrors.operatingUnit = 'Operating Unit is required';
        //if (!currency) newErrors.currency = 'Currency is required';
        //if (!paymentMethod) newErrors.paymentMethod = 'Payment Method is required';
        //if (!bankAccountNo) newErrors.bankAccountNo = 'Bank Account is required';
        if (!paymentTemplate) newErrors.paymentTemplate = 'Payment Template is required';
        if (!invoice) newErrors.invoice = 'Invoice is required';
        if (!expectedResult) newErrors.expectedResult = 'Expected Result is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const [isSubmitting, setIsSubmitting] = useState(false);
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!validateForm()) return;
        setIsSubmitting(true);
        const payload = {
            serviceType: 'OFA',
            project,
            description,
            country,
            operatingUnit,
            currency,
            paymentMethod,
            bankAccountNo,
            invoice,
            paymentTemplate,
            expectedResult
        };
        try {
            /*const res = await fetch(testPilotApi + createTestcase, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });*/
            const res = await makeApiRequest(testPilotApi, 'POST', createTestcase, payload)

            const data = await res.json();
            if (res.ok) {
                setAlert({ message: 'Test case created successfully!', type: 'success' });
                setErrors({});
                resetForm();
                onSaveSuccess?.();
            } else {
                setAlert({ message: `Error: ${data.message || 'Something went wrong.'}`, type: 'error' });
            }
        } catch {
            setAlert({ message: 'Submission error. Please try again.', type: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setProject('');
        setDescription('');
        setpaymentTemplate('');
        setBankAccountNo('');
        setCountry('');
        setOperatingUnit('');
        setCurrency('');
        setPaymentMethod('');
        setExpectedResult('');
    };

    const handleProjectChange = (value: string) => {
        setIsAddingProject(value.trim().toLowerCase() === 'add new project');
        setProject(value);
    };

    return (
        <div className="overlay-wrapper">
            {isSubmitting && (
                <div className="overlay">
                    <div className="overlay-message">Creating test case…</div>
                </div>
            )}
        <form onSubmit={handleSubmit} data-testid="ofa-form">
            {alert.message && (
                <KatAlert
                    header={alert.type === 'success' ? 'Success' : 'Error'}
                    description={alert.message}
                    variant={alert.type === 'success' ? 'success' : 'danger'}
                    dismissed={false}
                    persistent={true}
                />
            )}

            <div className="test-pilot-container test-case-section">
                <KatRow className="form-row">
                    <KatColumn xs={12}>
                        <KatAlert
                            className="alert"
                            header="Ensure required invoice for test payment is staged in OFA."
                            variant="info"
                        />
                    </KatColumn>
                </KatRow>

                <KatRow className="form-row">
                    <KatColumn xs={12}>
                        <h4>Create New OFA Test Case</h4>
                    </KatColumn>
                </KatRow>

                <div className="parent-container">
                    {!isAddingProject ? (
                        <KatRow className="form-row">
                            <KatColumn xs={12}>
                                <DropDownHelper query={projectQuery + getOfaProjects} label="Project" value={project} onChange={handleProjectChange} />
                            </KatColumn>
                        </KatRow>
                    ) : (
                        <KatRow className="form-row">
                            <KatColumn xs={12}>
                                <KatInput
                                    label="Enter New Project"
                                    type="text"
                                    value={project}
                                    onChange={(e: KatInput.ChangeEvent) => setProject(e.currentTarget.value)}
                                    state={errors.project ? 'error' : undefined}
                                    stateLabel={errors.project}
                                />
                            </KatColumn>
                        </KatRow>
                    )}

                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <KatInput
                                label="Test Description"
                                type="text"
                                value={description}
                                onChange={(e: KatInput.ChangeEvent) => setDescription(e.currentTarget.value)}
                                state={errors.description ? 'error' : undefined}
                                stateLabel={errors.description}
                            />
                        </KatColumn>
                    </KatRow>
                    {/*
                    <KatRow className="form-row">
                        <KatColumn xs={6}>
                            <DropDownHelper query={countryQuery} label="Payor Country" value={country} onChange={setCountry} />
                        </KatColumn>
                        <KatColumn xs={6}>
                            <DropDownHelper query={operatingUnitQuery} label="Operating Unit" value={operatingUnit} onChange={setOperatingUnit} />
                        </KatColumn>
                    </KatRow>

                    <KatRow className="form-row">
                        <KatColumn xs={6}>
                            <DropDownHelper query={currencyQuery} label="Payor Currency" value={currency} onChange={setCurrency} />
                        </KatColumn>
                        <KatColumn xs={6}>
                            <DropDownHelper query={paymentMethodQuery} label="Payment Method" value={paymentMethod} onChange={setPaymentMethod} />
                        </KatColumn>
                    </KatRow>
                    */}

                    <KatRow className="form-row">
                        {/*
                        <KatColumn xs={6}>
                            <KatInput
                                label="Payor Bank Account"
                                type="text"
                                value={bankAccountNo}
                                onChange={(e: KatInput.ChangeEvent) => setBankAccountNo(e.currentTarget.value)}
                                state={errors.bankAccountNo ? 'error' : undefined}
                                stateLabel={errors.bankAccountNo}
                            />
                        </KatColumn>
                        */}
                        <KatColumn xs={12}>
                            <KatInput
                                label="Payment Template Name"
                                type="text"
                                value={paymentTemplate}
                                onChange={(e: KatInput.ChangeEvent) => setpaymentTemplate(e.currentTarget.value)}
                                state={errors.paymentTemplate ? 'error' : undefined}
                                stateLabel={errors.paymentTemplate}
                            />
                        </KatColumn>
                    </KatRow>

                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <KatInput
                                label="Invoice Number"
                                type="text"
                                value={invoice}
                                onChange={(e: KatInput.ChangeEvent) => setInvoice(e.currentTarget.value)}
                                state={errors.invoice ? 'error' : undefined}
                                stateLabel={errors.invoice}
                            />
                        </KatColumn>
                    </KatRow>

                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <DropDownHelper query={expectedResultQuery} label="Expected Result" value={expectedResult} onChange={setExpectedResult} />
                        </KatColumn>
                    </KatRow>

                    <KatRow className="form-row">
                        <KatColumn xs={12}>
                            <div className="button-group">
                                <KatButton label="Cancel" variant="secondary" className="button" />
                                <KatButton type="submit" variant="primary" className="button" label="Save" />
                            </div>
                        </KatColumn>
                    </KatRow>
                </div>
            </div>
        </form>
        </div>
    );
};

export default OFATab;